import { useState, ReactNode } from "react";
import { MenuItem, SelectProps as MuiSelectProps } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronDown, ChevronUp } from "lucide-react";
import TextField from "./TextField";

interface Option {
  label: string;
  value: any;
}

type SelectWrapperProps<Value = string> = {
  options?: Option[];
  children?: ReactNode;
  variant?: "rounded" | "outlined" | "detachedOutlined" | "standard";
  value?: Value;
  onChange?: MuiSelectProps<Value>["onChange"];
  renderValue?: MuiSelectProps<Value>["renderValue"];
  IconComponent?: MuiSelectProps<Value>["IconComponent"];
  multiple?: boolean;
  displayEmpty?: boolean;
  open?: boolean;
  onOpen?: MuiSelectProps<Value>["onOpen"];
  onClose?: MuiSelectProps<Value>["onClose"];
  MenuProps?: MuiSelectProps<Value>["MenuProps"];
  [key: string]: any; // Allow other TextField props
};

const SelectWrapper = <Value = string,>({
  options,
  children,
  variant = "rounded",
  sx,
  value,
  onChange,
  renderValue,
  IconComponent,
  multiple,
  displayEmpty,
  open: openFromParent,
  onOpen: onOpenFromParent,
  onClose: onCloseFromParent,
  MenuProps,
  ...textFieldProps
}: SelectWrapperProps<Value>) => {
  const theme = useTheme();
  const isRoundedVariant = variant === "rounded";

  const [open, setOpen] = useState(false);

  const ChevronIconComponent = () => {
    const isOpen = isRoundedVariant ? open : openFromParent;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "20px",
          height: "20px",
          flexShrink: 0,
        }}
      >
        {isOpen ? (
          <ChevronUp
            size={20}
            strokeWidth="2px"
            color={theme.palette.text.tertiary}
          />
        ) : (
          <ChevronDown
            size={20}
            strokeWidth="2px"
            color={theme.palette.text.tertiary}
          />
        )}
      </div>
    );
  };

  const selectProps: Partial<MuiSelectProps<Value>> = {
    onChange,
    renderValue,
    multiple,
    displayEmpty,
    MenuProps,
    open: isRoundedVariant ? open : openFromParent,
    onOpen: isRoundedVariant ? () => setOpen(true) : onOpenFromParent,
    onClose: isRoundedVariant ? () => setOpen(false) : onCloseFromParent,
    IconComponent: IconComponent || ChevronIconComponent,
  };

  const menuItems =
    children ??
    options?.map((option, index) => (
      <MenuItem key={index} value={option.value}>
        {option.label}
      </MenuItem>
    ));

  const textFieldVariant = isRoundedVariant ? "outlined" : variant;

  return (
    <TextField
      {...textFieldProps}
      value={value}
      select
      variant={textFieldVariant}
      sx={
        isRoundedVariant
          ? {
              margin: "0px",
              padding: "0px",
              height: "40px",
              minHeight: "40px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "100px !important",
                height: "40px",
                minHeight: "40px",
              },
              "& .MuiSelect-select": {
                margin: "0px",
                padding: "0px",
                marginLeft: "20px",
                color: theme.palette.text.primary,
                fontSize: "14px",
                fontWeight: "500",
              },
              "& fieldset": {
                border: `1px solid ${theme.palette.border}`,
                borderRadius: "100px !important",
              },
              "& .MuiSelect-icon": {
                width: "20px",
                height: "20px",
                minWidth: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              cursor: "pointer",
              ...sx,
            }
          : {
              cursor: "pointer",
              "& .MuiSelect-icon": {
                width: "20px",
                height: "20px",
                minWidth: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              ...sx,
            }
      }
      SelectProps={selectProps as any}
    >
      {menuItems}
    </TextField>
  );
};

export default SelectWrapper;
